import { Component, OnInit, Inject } from '@angular/core';
import { ProductExperience, Commodity, ProductMaterial, ProductExperienceTool } from 'app/profile/models/site.model';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { Store } from '@ngrx/store';
import { getCommodities } from 'app/profile/store/app.selector';
import { ProfileService } from 'app/profile/services/profile.service';
import { ToolCategory, ToolSubcategory, Tool, ToolFilter } from 'app/profile/models/generic.model';
import { TslOption } from '@tesla/ng-tds';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { MaterialFormDialogComponent } from '../material-form-dialog/material-form-dialog.component';
import { I18nService } from 'app/shared/services/i18n.service';


@Component({
    selector: 'app-product-form-dialog',
    templateUrl: './product-form-dialog.component.html',
    styleUrls: ['./product-form-dialog.component.scss']
})
export class ProductFormDialogComponent implements OnInit {
    editForm: FormGroup;
    commodities: Commodity[] = [];
    categories: ToolCategory[] = [];
    subcategories: ToolSubcategory[] = [];
    tools: Tool[] = [];
    equipments: Tool[] = [];

    filteredEquipmentSubcategories$: BehaviorSubject<Array<ToolSubcategory>> = new BehaviorSubject([]);
    filteredToolSubcategories$: BehaviorSubject<Array<ToolSubcategory>> = new BehaviorSubject([]);

    selectedTool: Tool;

    get commodity() { return this.editForm.get('commodity'); }
    get years() { return this.editForm.get('years'); }

    constructor(private fb: FormBuilder,
        private service: ProfileService,
        private store: Store<SupplierState>,
        public dialogRef: MatDialogRef<ProductFormDialogComponent>,
        public matDialog: MatDialog,
        private translate: I18nService,
        @Inject(MAT_DIALOG_DATA) public data: { supplierCode: string, model: ProductExperience }) {
        this.editForm = this.fb.group({
            commodity: [, Validators.required],
            years: [, Validators.required],
            parts: [],
            revenue: [],
            volume: [],
            quote: [],
            doesProduction: [],
            doesPrototype: [],
            selectedTool: [],
        });
        this.loadResources();
    }

    ngOnInit() {
        if (this.data.model.id) {
            const model = this.data.model;
            this.editForm.patchValue({
                commodity: model.commodityId,
                years: model.experienceYears,
                parts: model.totalParts,
                revenue: model.revenuePercentage,
                volume: model.volume,
                quote: model.quoteWeeks,
                doesProduction: model.doesProduction,
                doesPrototype: model.doesPrototype,
            });
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.editForm.invalid) {
            return;
        }

        this.data.model.commodityId = this.editForm.get('commodity').value;
        this.data.model.commodityDescription = this.getCommodity(this.data.model.commodityId);
        this.data.model.experienceYears = this.editForm.get('years').value;
        this.data.model.totalParts = this.editForm.get('parts').value;
        this.data.model.revenuePercentage = this.editForm.get('revenue').value;
        this.data.model.volume = this.editForm.get('volume').value;
        this.data.model.quoteWeeks = this.editForm.get('quote').value;
        this.data.model.doesProduction = this.editForm.get('doesProduction').value;
        this.data.model.doesPrototype = this.editForm.get('doesPrototype').value;
        this.dialogRef.close(this.data.model);
    }

    onSelectCategory(option: TslOption, isTool: boolean): void {
        const categoryId = parseInt(option.value, 0);
        if (isTool) {
            this.filteredToolSubcategories$.next(this.subcategories.filter(s => s.toolCategoryId === categoryId));
        } else {
            this.filteredEquipmentSubcategories$.next(this.subcategories.filter(s => s.toolCategoryId === categoryId));
        }
    }

    onSelectSubcategory(option: TslOption, isTool: boolean): void {
        const subCategoryId = parseInt(option.value, 0);
        if (isTool) {
            // tool
        } else {
            // equipment
        }
    }

    onAddTool(isTool: boolean): void {
        if (isTool) {
            if (this.selectedTool &&
                this.data.model.tools.findIndex(t => t.toolId === this.selectedTool.id &&
                    t.toolType === this.selectedTool.toolType) === -1) {
                const newTool = new ProductExperienceTool();
                newTool.isTool = true;
                newTool.toolId = this.selectedTool.id;
                newTool.toolName = this.selectedTool.data.name;
                newTool.toolType = this.selectedTool.toolType;

                this.data.model.tools.push(newTool);
            }
        } else {
            if (this.selectedTool &&
                this.data.model.equipments.findIndex(t => t.toolId === this.selectedTool.id &&
                    t.toolType === this.selectedTool.toolType) === -1) {
                const newTool = new ProductExperienceTool();
                newTool.isTool = false;
                newTool.toolId = this.selectedTool.id;
                newTool.toolName = this.selectedTool.data.name;
                newTool.toolType = this.selectedTool.toolType;

                this.data.model.equipments.push(newTool);
            }
        }
        this.selectedTool = null;
    }

    onRemoveTool(isTool: boolean, toolId: number): void {
        if (isTool) {
            const index = this.data.model.tools.findIndex(t => t.toolId === toolId);
            this.data.model.tools.splice(index, 1);
        } else {
            const index = this.data.model.equipments.findIndex(t => t.toolId === toolId);
            this.data.model.equipments.splice(index, 1);
        }
    }

    onAddMaterial(isTool: boolean, toolId: number): void {
        const newMat = new ProductMaterial();
        newMat.toolId = toolId;
        this.openMaterialDialog(newMat, isTool);
    }

    onRemoveMaterial(toolId: number, index: number, isTool: boolean): void {
        if (isTool) {
            const toolIndex = this.data.model.tools.findIndex(t => t.toolId === toolId);
            if (toolIndex !== -1) {
                this.data.model.tools[toolIndex].materials.splice(index, 1);
            }
        } else {
            const toolIndex = this.data.model.equipments.findIndex(t => t.toolId === toolId);
            if (toolIndex !== -1) {
                this.data.model.equipments[toolIndex].materials.splice(index, 1);
            }
        }
    }

    private openMaterialDialog(model: ProductMaterial, isTool: boolean = false): void {
        const dialogRef = this.matDialog.open(MaterialFormDialogComponent, { width: '400px', data: { isTool: isTool, model: model } });

        dialogRef.afterClosed()
            .subscribe(data => {
                if (!data.model) {
                    return;
                }

                if (data.isTool) {
                    const index = this.data.model.tools.findIndex(t => t.toolId === data.model.toolId);
                    if (index !== -1) {
                        this.data.model.tools[index].materials.push(data.model);
                    }
                } else {
                    const index = this.data.model.equipments.findIndex(t => t.toolId === data.model.toolId);
                    if (index !== -1) {
                        this.data.model.equipments[index].materials.push(data.model);
                    }
                }
            });
    }

    private getCommodity(id: number): string {
        const index = this.commodities.findIndex(c => c.id === id);
        return index > -1 ? this.commodities[index].description : '';
    }

    private loadResources(): void {
        this.store.select(getCommodities).subscribe(res => this.commodities = res);
        this.service.getCategories().subscribe(res => this.categories = res);
        this.service.getSubcategories(this.data.supplierCode, 0).subscribe(res => this.subcategories = res);

        const toolFilter = new ToolFilter();
        toolFilter.isEquipment = false;
        toolFilter.take = 1000;
        combineLatest(
            [this.service.searchTools(this.data.supplierCode, toolFilter),
            this.service.searchManufacturingTools(this.data.supplierCode, toolFilter)])
            .subscribe(([res, mfgTools]) => {
                if (res && mfgTools) {
                    this.tools = res.items;
                    this.tools.push(...mfgTools);
                }
            });

        const equipFilter = new ToolFilter();
        equipFilter.isEquipment = true;
        equipFilter.take = 1000;
        combineLatest(
            [this.service.searchTools(this.data.supplierCode, equipFilter),
            this.service.searchManufacturingTools(this.data.supplierCode, toolFilter)])
            .subscribe(([res, mfgEquipments]) => {
                if (res && mfgEquipments) {
                    this.equipments = res.items;
                    this.equipments.push(...mfgEquipments);
                }
            });
    }
}
